/**
 * Structured editor for a dataset's workflow: ordered columns, each holding
 * the tracker status names that map into it.
 *
 * Replaces a raw JSON textarea. The thing being edited is a mapping from the
 * tracker's **exact** status strings to shipcast's columns, and two properties
 * make hand-typing it a bad interface:
 *
 * - A status name has to match the tracker byte-for-byte. A wrong one is not
 *   rejected — it is simply never matched, so the items carrying it never
 *   reach the departure column and the forecast is quietly wrong. The failure
 *   surfaces one ingest later as an `unknown_statuses` warning.
 * - Some status names cannot be typed correctly at all. A name with a
 *   trailing non-breaking space reads identically to one without
 *   (shipcast-78m.1). Offering the observed string as something to *click* is
 *   the only way a user can ever map it.
 *
 * So the editor's job is to turn "recall and retype" into "recognise and
 * pick", and its status suggestions are the raw names the last ingest
 * actually saw. Free text stays available — a column may be configured before
 * any ingest has run — but it is the fallback, not the primary path.
 *
 * The JSON escape hatch is kept deliberately: it is the only way to work on a
 * workflow this editor cannot represent, and the only thing to do with a
 * config that fails to parse.
 */

import { useId, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2, X } from 'lucide-react';
import { Button } from '@lean-agility/design-system/react';

import { InlineInput } from '@/components/atoms';
import { SearchableSelect } from '@/components/molecules';

import { parseWorkflowColumns, serializeWorkflowColumns, type WorkflowColumnDraft } from '../workflowJson';

export function WorkflowEditor({
  value,
  onChange,
  observedStatuses,
  disabled,
  error,
}: {
  value: string;
  onChange: (json: string) => void;
  /** Raw status names the last ingest saw but no column claimed. */
  observedStatuses: string[];
  disabled: boolean;
  error?: string;
}) {
  const jsonId = useId();
  const columns = useMemo(() => parseWorkflowColumns(value), [value]);
  const unrepresentable = columns === null;
  const [showJson, setShowJson] = useState(false);
  // A workflow this editor cannot represent has to be shown as JSON — there is
  // nothing else to render, and hiding it would strand the config.
  const jsonMode = showJson || unrepresentable;

  const commit = (next: WorkflowColumnDraft[]) => onChange(serializeWorkflowColumns(next));

  const mapped = useMemo(() => new Set((columns ?? []).flatMap((c) => c.statuses)), [columns]);
  // Every status this editor has seen mapped. The last sync reports only the
  // statuses NO column claimed, so one removed here — or lost with its column —
  // would otherwise never be offered again until the next ingest
  // (shipcast-45y.7). Grown during render: React's pattern for state derived
  // from previous renders.
  const [seenMapped, setSeenMapped] = useState<readonly string[]>(() => [...mapped]);
  const newlySeen = [...mapped].filter((s) => !seenMapped.includes(s));
  if (newlySeen.length > 0) setSeenMapped([...seenMapped, ...newlySeen]);
  // A status belongs to exactly one column, so anything already mapped drops
  // out of the suggestions rather than inviting a contradiction.
  const suggestions = useMemo(
    () => [...new Set([...observedStatuses, ...seenMapped])].filter((s) => !mapped.has(s)),
    [observedStatuses, seenMapped, mapped],
  );

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="block text-sm font-medium">Workflow</span>
        <Button
          variant="ghost"
          type="button"
          size="sm"
          onClick={() => setShowJson((v) => !v)}
          disabled={disabled || unrepresentable}
        >
          {jsonMode ? 'Edit as columns' : 'Edit as JSON'}
        </Button>
      </div>

      {unrepresentable && (
        <p className="mb-1 text-xs text-feedback-warning">
          This workflow is not in the shape the column editor understands, so it is shown as JSON. Saving valid{' '}
          <code>{'{"columns": [{"name": …, "statuses": […]}]}'}</code> switches the editor back on.
        </p>
      )}

      {jsonMode ? (
        <textarea
          id={jsonId}
          aria-label="Workflow (JSON)"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={8}
          className="
            w-full rounded-sm border px-3 py-2 font-mono text-xs
            disabled:bg-bg-muted
          "
          disabled={disabled}
        />
      ) : (
        <div className="space-y-2">
          {columns.map((column, index) => (
            <ColumnEditor
              key={index}
              column={column}
              index={index}
              total={columns.length}
              suggestions={suggestions}
              disabled={disabled}
              onChange={(next) => commit(columns.map((c, i) => (i === index ? next : c)))}
              onRemove={() => commit(columns.filter((_, i) => i !== index))}
              onMove={(delta) => {
                const target = index + delta;
                if (target < 0 || target >= columns.length) return;
                const next = [...columns];
                const moved = next[index];
                const displaced = next[target];
                if (!moved || !displaced) return;
                next[index] = displaced;
                next[target] = moved;
                commit(next);
              }}
            />
          ))}
          {columns.length === 0 && (
            <p className="text-xs text-text-muted">
              No columns yet. Add one, or start from a board to get this filled in.
            </p>
          )}
          <Button
            variant="secondary"
            type="button"
            size="sm"
            onClick={() => commit([...columns, { name: '', statuses: [] }])}
            disabled={disabled}
          >
            <Plus className="size-4" /> Add column
          </Button>
        </div>
      )}
      {error && <p className="mt-1 text-xs text-feedback-error">{error}</p>}
    </div>
  );
}

/** One column row: its name, its statuses, and its position controls. */
function ColumnEditor({
  column,
  index,
  total,
  suggestions,
  disabled,
  onChange,
  onRemove,
  onMove,
}: {
  column: WorkflowColumnDraft;
  index: number;
  total: number;
  suggestions: string[];
  disabled: boolean;
  onChange: (next: WorkflowColumnDraft) => void;
  onRemove: () => void;
  onMove: (delta: number) => void;
}) {
  const [freeText, setFreeText] = useState('');
  const suggestId = useId();
  const position = `${index + 1}`;

  const addStatus = (status: string) => {
    const trimmed = status.trim();
    // Not trimmed on the way in from a SUGGESTION — those are the tracker's
    // exact strings and may legitimately end in whitespace. Only free text is
    // trimmed, where surrounding space is a typo rather than data.
    if (!trimmed || column.statuses.includes(status)) return;
    onChange({ ...column, statuses: [...column.statuses, status] });
  };

  return (
    <div className="rounded-sm border border-border-default p-2">
      <div className="flex items-center gap-2">
        <InlineInput
          size="sm"
          aria-label={`Column ${position} name`}
          value={column.name}
          onChange={(e) => onChange({ ...column, name: e.target.value })}
          placeholder="Column name"
          disabled={disabled}
        />
        <Button
          variant="ghost"
          type="button"
          size="sm"
          aria-label={`Move column ${position} earlier`}
          onClick={() => onMove(-1)}
          disabled={disabled || index === 0}
        >
          <ArrowUp className="size-4" />
        </Button>
        <Button
          variant="ghost"
          type="button"
          size="sm"
          aria-label={`Move column ${position} later`}
          onClick={() => onMove(1)}
          disabled={disabled || index === total - 1}
        >
          <ArrowDown className="size-4" />
        </Button>
        <Button
          variant="ghost"
          type="button"
          size="sm"
          aria-label={`Delete column ${position}`}
          onClick={onRemove}
          disabled={disabled}
        >
          <Trash2 className="size-4" />
        </Button>
      </div>

      <div className="mt-2 flex flex-wrap gap-1">
        {column.statuses.map((status) => (
          <span
            key={status}
            className="inline-flex items-center gap-1 rounded-sm bg-bg-muted px-2 py-0.5 font-mono text-xs"
          >
            {status}
            <button
              type="button"
              aria-label={`Remove status ${status}`}
              onClick={() => onChange({ ...column, statuses: column.statuses.filter((s) => s !== status) })}
              disabled={disabled}
              className="
                text-text-muted
                hover:text-text-primary
              "
            >
              <X className="size-3" />
            </button>
          </span>
        ))}
        {column.statuses.length === 0 && <span className="text-xs text-text-muted">No statuses mapped yet.</span>}
      </div>

      <div className="mt-2 flex items-end gap-2">
        <div className="flex-1">
          {/* `SearchableSelect` is labelled by id, not aria-label; the label is
              visually hidden because the column above already names the group. */}
          <label htmlFor={suggestId} className="sr-only">
            {`Add an observed status to column ${position}`}
          </label>
          <SearchableSelect
            id={suggestId}
            value={null}
            onChange={(v) => v && addStatus(v)}
            options={suggestions.map((s) => ({ value: s, label: s }))}
            placeholder="Add a known status"
            searchPlaceholder="Search statuses..."
            noOptionsMessage="No unmapped statuses known"
            disabled={disabled || suggestions.length === 0}
          />
        </div>
        <InlineInput
          size="sm"
          aria-label={`Add a status by name to column ${position}`}
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          placeholder="…or type one"
          disabled={disabled}
        />
        <Button
          variant="secondary"
          type="button"
          size="sm"
          onClick={() => {
            addStatus(freeText.trim());
            setFreeText('');
          }}
          disabled={disabled || !freeText.trim()}
        >
          Add
        </Button>
      </div>
    </div>
  );
}
